import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

const LAYOUT_GROUPED_BY_CASHIER = 'grouped-by-cashier';
const LAYOUT_PAYMENT_SUMMARY = 'payment-summary';

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function asNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   const amount = Number(value);
   return Number.isFinite(amount) ? amount : null;
}

function formatCurrency(value: any): string {
   const amount = asNumberOrNull(value);
   if (amount === null) return asString(value);

   const formatted = Math.abs(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return amount < 0 ? `-$${formatted}` : `$${formatted}`;
}

/**
 * Temporary frontend display helper.
 * Eventually these descriptions should come from the backend / task generator data.
 */
const TRANSACTION_CODE_DESCRIPTIONS: Record<string, string> = {
   '8000': 'Paid Out*',
   '8001': 'Gratuity*',
   '9000': 'Cash',
   '9003': 'Direct Bill',
   '9104': 'American Express - OPI',
   '9105': 'American Express POS',
   '9124': 'Visa Card - EFT',
   '9125': 'Visa POS',
   '9134': 'Mastercard - EFT',
   '9135': 'Master Card POS',
   '9161': 'EFTPOS POS',
   '9164': 'EFTPOS - OPI',
   '9190': 'Direct Bank Deposit',
   '9400': 'T/A Prepaid commission',
   '9405': 'T/A Prepaid Commission Fore',
   '9950': 'Cash Drop'
};

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function getCashiers(reportJson: any): any[] {
   const parsedReportJson = parseReportJson(reportJson);

   return Array.isArray(parsedReportJson?.CashierSummary)
      ? parsedReportJson.CashierSummary
      : [];
}

function getCashierGroupKey(cashier: any): string {
   return (
      asString(cashier?.CashierId) ||
      asString(cashier?.TyallsRowId) ||
      asString(cashier?.tyallsRowId)
   );
}

function getCashierRowId(cashier: any): string {
   return (
      asString(cashier?.TyallsRowId) ||
      asString(cashier?.tyallsRowId) ||
      `cashier:${asString(cashier?.CashierId)}`
   );
}

function getTransactionRowId(cashier: any, summary: any): string {
   return (
      asString(summary?.TyallsRowId) ||
      asString(summary?.tyallsRowId) ||
      `${getCashierRowId(cashier)}:transaction:${asString(
         summary?.TransactionCode
      )}:${asString(summary?.TransactionCodeTotal)}`
   );
}

function getTransactionDescription(summary: any): string {
   const savedDescription = asString(summary?.Description).trim();
   if (savedDescription) return savedDescription;

   const transactionCode = asString(summary?.TransactionCode);
   return TRANSACTION_CODE_DESCRIPTIONS[transactionCode] ?? '';
}

function getTransactionNet(summary: any): number | null {
   return (
      asNumberOrNull(summary?.Net) ??
      asNumberOrNull(summary?.TransactionCodeTotal) ??
      asNumberOrNull(summary?.LocalAmount)
   );
}

function joinFolioReferences(value: any): string {
   if (!Array.isArray(value)) return '';

   return value.map(asString).filter(Boolean).join('   •   ');
}

function groupCashiers(cashiers: any[]): any[] {
   const grouped = new Map<string, any>();

   for (const cashier of cashiers) {
      const key = getCashierGroupKey(cashier);
      if (!key) continue;

      const existing = grouped.get(key);

      if (!existing) {
         grouped.set(key, {
            ...cashier,
            TransactionSummaries: Array.isArray(cashier?.TransactionSummaries)
               ? [...cashier.TransactionSummaries]
               : []
         });
         continue;
      }

      const existingSummaries = Array.isArray(existing.TransactionSummaries)
         ? existing.TransactionSummaries
         : [];

      const newSummaries = Array.isArray(cashier?.TransactionSummaries)
         ? cashier.TransactionSummaries
         : [];

      existing.TransactionSummaries = [...existingSummaries, ...newSummaries];

      const existingTotal = asNumberOrNull(existing.CashierTotal) ?? 0;
      const nextTotal = asNumberOrNull(cashier?.CashierTotal) ?? 0;
      existing.CashierTotal = existingTotal + nextTotal;
   }

   return Array.from(grouped.values()).sort((a, b) => {
      const aId = Number(a?.CashierId);
      const bId = Number(b?.CashierId);

      if (Number.isFinite(aId) && Number.isFinite(bId)) {
         return aId - bId;
      }

      return asString(a?.CashierId).localeCompare(asString(b?.CashierId));
   });
}

function sortTransactionSummaries(summaries: any[]): any[] {
   return [...summaries].sort((a, b) => {
      const aCode = Number(a?.TransactionCode);
      const bCode = Number(b?.TransactionCode);

      if (Number.isFinite(aCode) && Number.isFinite(bCode)) {
         return aCode - bCode;
      }

      return asString(a?.TransactionCode).localeCompare(asString(b?.TransactionCode));
   });
}

function flattenGroupedByCashier(reportJson: any): ReportRowVm[] {
   const cashiers = groupCashiers(getCashiers(reportJson));
   const rows: ReportRowVm[] = [];

   for (const cashier of cashiers) {
      const cashierNo = asString(cashier?.CashierId);
      const cashierName = asString(cashier?.CashierName ?? cashier?.UserName);
      const cashierTotalRaw = asNumberOrNull(cashier?.CashierTotal);

      const cashierDisplay = cashierName
         ? `CASHIER: ${cashierNo}  — ${cashierName}`
         : `CASHIER: ${cashierNo}`;

      rows.push({
         rowId: `cashier-summary:${LAYOUT_GROUPED_BY_CASHIER}:cashier:${getCashierRowId(cashier)}:header`,
         rowClass: 'report-group-header-row',
         isCheckable: false,
         cells: {
            rowType: 'groupHeader',
            transactionCode: cashierDisplay,
            folioReferences: '',
            debit: '',
            debitRaw: null,
            credit: '',
            creditRaw: null,
            netTotal: formatCurrency(cashierTotalRaw),
            netTotalRaw: cashierTotalRaw
         }
      });

      const transactionSummaries = Array.isArray(cashier?.TransactionSummaries)
         ? sortTransactionSummaries(cashier.TransactionSummaries)
         : [];

      for (const summary of transactionSummaries) {
         const debitRaw = asNumberOrNull(summary?.Debit) ?? 0;
         const creditRaw = asNumberOrNull(summary?.Credit) ?? 0;
         const netTotalRaw = getTransactionNet(summary);

         rows.push({
            rowId: getTransactionRowId(cashier, summary),
            cells: {
               rowType: 'transaction',
               transactionCode: asString(summary?.TransactionCode),
               transactionCodeRaw: asString(summary?.TransactionCode),
               paymentType: getTransactionDescription(summary),
               folioReferences: joinFolioReferences(summary?.FolioReferences),
               debit: formatCurrency(debitRaw),
               debitRaw,
               credit: formatCurrency(creditRaw),
               creditRaw,
               netTotal: formatCurrency(netTotalRaw),
               netTotalRaw
            }
         });
      }
   }

   return rows;
}

function flattenPaymentSummary(reportJson: any): ReportRowVm[] {
   const cashiers = groupCashiers(getCashiers(reportJson));

   const summaryByCode = new Map<
      string,
      {
         code: string;
         paymentType: string;
         totalNet: number;
      }
   >();

   for (const cashier of cashiers) {
      const transactionSummaries = Array.isArray(cashier?.TransactionSummaries)
         ? cashier.TransactionSummaries
         : [];

      for (const summary of transactionSummaries) {
         const code = asString(summary?.TransactionCode);
         if (!code) continue;

         const existing = summaryByCode.get(code) ?? {
            code,
            paymentType: getTransactionDescription(summary),
            totalNet: 0
         };

         if (!existing.paymentType) {
            existing.paymentType = getTransactionDescription(summary);
         }

         existing.totalNet += getTransactionNet(summary) ?? 0;
         summaryByCode.set(code, existing);
      }
   }

   return Array.from(summaryByCode.values())
      .sort((a, b) => {
         const aCode = Number(a.code);
         const bCode = Number(b.code);

         if (Number.isFinite(aCode) && Number.isFinite(bCode)) {
            return aCode - bCode;
         }

         return a.code.localeCompare(b.code);
      })
      .map((summary) => ({
         rowId: `cashier-summary:${LAYOUT_PAYMENT_SUMMARY}:transaction-code:${summary.code}`,
         cells: {
            rowType: 'paymentSummary',
            code: summary.code,
            codeRaw: summary.code,
            paymentType:
               summary.paymentType ||
               TRANSACTION_CODE_DESCRIPTIONS[summary.code] ||
               '',
            totalNet: formatCurrency(summary.totalNet),
            totalNetRaw: summary.totalNet
         }
      }));
}

function getHeaderTotal(rows: ReportRowVm[], layoutId?: string | null): number {
   if (layoutId === LAYOUT_PAYMENT_SUMMARY) {
      return rows.reduce((total, row) => {
         const amount = Number(row.cells?.['totalNetRaw']);
         return Number.isFinite(amount) ? total + amount : total;
      }, 0);
   }

   return rows.reduce((total, row) => {
      if (row.cells?.['rowType'] !== 'groupHeader') return total;

      const amount = Number(row.cells?.['netTotalRaw']);
      return Number.isFinite(amount) ? total + amount : total;
   }, 0);
}

export const CASHIER_SUMMARY_DEFINITION: ReportDefinition = {
   reportType: 'CashierSummary',
   version: 2,
   title: 'Cashier Summary',

   header: {
      title: 'Cashier Summary',
      snippets: ({ allRows, layoutId }) => {
         const count =
            layoutId === LAYOUT_PAYMENT_SUMMARY
               ? allRows.filter((row) => row.cells?.['rowType'] === 'paymentSummary')
                    .length
               : allRows.filter((row) => row.cells?.['rowType'] === 'groupHeader')
                    .length;

         const totalAmount = getHeaderTotal(allRows, layoutId);

         return [
            {
               label:
                  layoutId === LAYOUT_PAYMENT_SUMMARY ? 'Payment Types' : 'Cashiers',
               value: count
            },
            {
               label: 'Total Amount',
               value: formatCurrency(totalAmount),
               tone: totalAmount < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      excludeRowTypes: ['groupHeader', 'groupTotal', 'cashierTotal']
   },

   annotations: false,

   /**
    * Single-layout fallback.
    * The viewer will use layouts below when available.
    */
   columns: [
      { key: 'transactionCode', header: 'TRN CODE', width: '170px' },
      {
         key: 'folioReferences',
         header: 'FOLIO REFERENCES',
         width: '520px',
         maxChars: 120
      },
      { key: 'debit', header: 'DEBIT', width: '150px' },
      { key: 'credit', header: 'CREDIT', width: '150px' },
      { key: 'netTotal', header: 'NET TOTAL', width: '160px' }
   ],

   flattenRows: flattenGroupedByCashier,

   filter: {
      fields: [
         { key: 'transactionCode', label: 'Transaction Code', type: 'string' },
         { key: 'paymentType', label: 'Payment Type', type: 'string' },
         { key: 'folioReferences', label: 'Folio References', type: 'string' },
         {
            key: 'debit',
            label: 'Debit',
            type: 'number',
            getValue: (row) => row.cells['debitRaw']
         },
         {
            key: 'credit',
            label: 'Credit',
            type: 'number',
            getValue: (row) => row.cells['creditRaw']
         },
         {
            key: 'netTotal',
            label: 'Net Total',
            type: 'number',
            getValue: (row) => row.cells['netTotalRaw']
         }
      ]
   },

   layouts: [
      {
         id: LAYOUT_GROUPED_BY_CASHIER,
         label: 'Grouped by Cashier',
         sorting: false,
         annotations: {
            enabled: true,
            selectableRowTypes: ['transaction'],
            disabledMessage:
               'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
         },
         columns: [
            { key: 'transactionCode', header: 'TRN CODE', width: '170px' },
            {
               key: 'folioReferences',
               header: 'FOLIO REFERENCES',
               width: '520px',
               maxChars: 120
            },
            { key: 'debit', header: 'DEBIT', width: '150px' },
            { key: 'credit', header: 'CREDIT', width: '150px' },
            { key: 'netTotal', header: 'NET TOTAL', width: '160px' }
         ],
         flattenRows: flattenGroupedByCashier,
         filter: {
            fields: [
               { key: 'transactionCode', label: 'Transaction Code', type: 'string' },
               { key: 'paymentType', label: 'Payment Type', type: 'string' },
               { key: 'folioReferences', label: 'Folio References', type: 'string' },
               {
                  key: 'debit',
                  label: 'Debit',
                  type: 'number',
                  getValue: (row) => row.cells['debitRaw']
               },
               {
                  key: 'credit',
                  label: 'Credit',
                  type: 'number',
                  getValue: (row) => row.cells['creditRaw']
               },
               {
                  key: 'netTotal',
                  label: 'Net Total',
                  type: 'number',
                  getValue: (row) => row.cells['netTotalRaw']
               }
            ]
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['transaction']
         }
      },
      {
         id: LAYOUT_PAYMENT_SUMMARY,
         label: 'Payment Summary',
         sorting: {
            enabled: true,
            persist: true
         },
         annotations: {
            enabled: false,
            disabledMessage:
               'Row comments and row highlighting are not available for Payment Summary because this layout is calculated from multiple rows. Please use the main report comments panel to add comments for this report.'
         },
         columns: [
            {
               key: 'code',
               header: 'CODE',
               width: '140px',
               sort: {
                  type: 'string',
                  getValue: (row) => row.cells['codeRaw']
               }
            },
            {
               key: 'paymentType',
               header: 'PAYMENT TYPE',
               width: '360px',
               sort: { type: 'string' }
            },
            {
               key: 'totalNet',
               header: 'TOTAL NET',
               width: '180px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['totalNetRaw'],
                  defaultDirection: 'desc'
               }
            }
         ],
         flattenRows: flattenPaymentSummary,
         filter: {
            fields: [
               { key: 'code', label: 'Code', type: 'string' },
               { key: 'paymentType', label: 'Payment Type', type: 'string' },
               {
                  key: 'totalNet',
                  label: 'Total Net',
                  type: 'number',
                  getValue: (row) => row.cells['totalNetRaw']
               }
            ]
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['paymentSummary']
         }
      }
   ]
};
